import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, Video, MapPin, Clock, ExternalLink, X, Plus, Settings, Loader, CheckCircle, AlertCircle, User, BookOpen, Globe, Mail, Users, Link, Copy, Lock, Check } from 'lucide-react';
import { auth, db } from '../../lib/firebase';
import { doc } from 'firebase/firestore';
import { secureGetDoc } from '../../lib/secureApplicationsRepo';
import Modal from './Modal';
import ApplicantPreviewModal from '../ApplicantPreviewModal';
import { Application } from '../../types';
import SchedulingCalendar from './SchedulingCalendar';
import SearchableSelect from './SearchableSelect';
import { confirmDialog } from '../../lib/confirmStore';
import { notifyToast } from '../../lib/toastStore';

const ALL_TIMEZONES = (() => {
  try {
    if (typeof Intl !== 'undefined' && typeof (Intl as any).supportedValuesOf === 'function') {
      const tzs = (Intl as any).supportedValuesOf('timeZone');
      if (Array.isArray(tzs) && tzs.length > 0) return tzs;
    }
  } catch {}
  return [
    'Africa/Abidjan', 'Africa/Accra', 'Africa/Addis_Ababa', 'Africa/Algiers', 'Africa/Cairo', 'Africa/Casablanca', 'Africa/Johannesburg', 'Africa/Lagos', 'Africa/Nairobi', 'Africa/Tunis',
    'America/Anchorage', 'America/Argentina/Buenos_Aires', 'America/Bogota', 'America/Caracas', 'America/Chicago', 'America/Denver', 'America/Guatemala', 'America/Halifax', 'America/Los_Angeles', 'America/Mexico_City', 'America/New_York', 'America/Phoenix', 'America/Santiago', 'America/Sao_Paulo', 'America/Toronto', 'America/Vancouver',
    'Asia/Almaty', 'Asia/Amman', 'Asia/Baghdad', 'Asia/Baku', 'Asia/Bangkok', 'Asia/Beirut', 'Asia/Colombo', 'Asia/Dhaka', 'Asia/Dubai', 'Asia/Hong_Kong', 'Asia/Jakarta', 'Asia/Jerusalem', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Kuala_Lumpur', 'Asia/Kuwait', 'Asia/Manila', 'Asia/Riyadh', 'Asia/Seoul', 'Asia/Shanghai', 'Asia/Singapore', 'Asia/Taipei', 'Asia/Tashkent', 'Asia/Tehran', 'Asia/Tokyo',
    'Atlantic/Azores', 'Atlantic/Canary', 'Atlantic/Reykjavik',
    'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Darwin', 'Australia/Melbourne', 'Australia/Perth', 'Australia/Sydney',
    'Europe/Amsterdam', 'Europe/Athens', 'Europe/Belgrade', 'Europe/Berlin', 'Europe/Brussels', 'Europe/Bucharest', 'Europe/Budapest', 'Europe/Copenhagen', 'Europe/Dublin', 'Europe/Helsinki', 'Europe/Istanbul', 'Europe/Kiev', 'Europe/Lisbon', 'Europe/London', 'Europe/Madrid', 'Europe/Moscow', 'Europe/Oslo', 'Europe/Paris', 'Europe/Prague', 'Europe/Rome', 'Europe/Stockholm', 'Europe/Vienna', 'Europe/Warsaw', 'Europe/Zurich',
    'Pacific/Auckland', 'Pacific/Fiji', 'Pacific/Honolulu', 'Pacific/Tongatapu',
    'UTC'
  ];
})();

interface MyEvent {
  eventId: string;
  title: string;
  eventType: string;
  dateTime: string;
  timezone?: string;
  location?: string;
  description?: string;
  status: string;
  isVirtual?: boolean;
  meetingUrl?: string;
  meetingProvider?: string;
  assignedTo?: string;
  assignedToName?: string;
  assignedTeam?: { uid: string; name: string; email?: string; role?: string }[];
  applicantId?: string;
  isSchedulingRequest?: boolean;
  isGroupEvent?: boolean;
  groupEventConfig?: any;
  slots?: any[];
}

const STATUS_STYLES: Record<string, string> = {
  Scheduled: 'bg-blue-50 text-blue-700',
  'Awaiting Response': 'bg-amber-50 text-amber-700',
  Pending: 'bg-amber-50 text-amber-700',
  Confirmed: 'bg-emerald-50 text-emerald-700',
  Rescheduled: 'bg-indigo-50 text-indigo-700',
  Completed: 'bg-emerald-50 text-emerald-700',
  Cancelled: 'bg-rose-50 text-rose-700',
  Declined: 'bg-rose-50 text-rose-700',
};
const ACTIVE_STATUSES = ['Scheduled', 'Confirmed', 'Pending', 'Awaiting Response'];
const STATUS_OPTIONS = ['Scheduled', 'Pending', 'Awaiting Response', 'Confirmed', 'Rescheduled', 'Completed', 'Cancelled', 'Declined'];

const groupSlotCount = (ev: MyEvent) => (Array.isArray(ev.slots) ? ev.slots.length : 0);
const groupBookedCount = (ev: MyEvent) => (Array.isArray(ev.slots) ? ev.slots.filter(s => s && (s.bookingStatus === 'booked' || s.bookedBy)).length : 0);
const groupTeamNames = (ev: MyEvent) => (Array.isArray(ev.assignedTeam) ? ev.assignedTeam.map((m: any) => m.name || m.uid).filter(Boolean) : []);

export default function MyEventsView({ onViewApplicant, userRole = 'admin' }: { onViewApplicant?: (app: Application) => void; userRole?: string }) {
  const [events, setEvents] = useState<MyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'upcoming' | 'past' | 'all'>('upcoming');
  const [layoutView, setLayoutView] = useState<'list' | 'calendar' | 'availability'>('list');
  const [selectedEvent, setSelectedEvent] = useState<MyEvent | null>(null);
  const [showAvailability, setShowAvailability] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [savingStatus, setSavingStatus] = useState(false);
  const [applicantApp, setApplicantApp] = useState<Application | null>(null);
  const [applicantLoading, setApplicantLoading] = useState(false);
  const [showApplicantPreview, setShowApplicantPreview] = useState(false);

  // Load the applicant's application (client-side secure read decrypts PII
  // like name/email/country) for the selected event.
  const loadApplicant = useCallback(async (applicantId?: string) => {
    setApplicantApp(null);
    setShowApplicantPreview(false);
    if (!applicantId) { setApplicantLoading(false); return; }
    setApplicantLoading(true);
    try {
      const snap = await secureGetDoc(doc(db, 'applications', applicantId));
      if (snap.exists()) {
        setApplicantApp({ ...snap.data(), applicationId: snap.id } as Application);
      }
    } catch (err) {
      console.warn('Failed to load applicant for event:', err);
    }
    setApplicantLoading(false);
  }, []);

  const fetchMyEvents = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) { if (!isSilent) setLoading(false); return; }
      const res = await fetch('/api/events/mine', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const activeEvents = (data.events || []).filter((e: any) => !e.archived && e.status !== 'Archived');
        setEvents(activeEvents);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
    if (!isSilent) setLoading(false);
  }, []);

  useEffect(() => { fetchMyEvents(false); }, [fetchMyEvents]);

  // Background auto-refresh without flickering loading spinners or layout jumps
  useEffect(() => {
    const timer = setInterval(() => { fetchMyEvents(true); }, 30000);
    return () => clearInterval(timer);
  }, [fetchMyEvents]);

  const now = new Date();
  const filteredEvents = events.filter(ev => {
    if (viewMode === 'upcoming') return ev.dateTime && new Date(ev.dateTime) >= now && ACTIVE_STATUSES.includes(ev.status || '');
    if (viewMode === 'past') return !ev.dateTime || new Date(ev.dateTime) < now || ['Completed', 'Cancelled', 'Declined'].includes(ev.status || '');
    return true;
  }).sort((a, b) => new Date(a.dateTime || 0).getTime() - new Date(b.dateTime || 0).getTime());

  // Organizer / moderator actions — the server enforces that only the
  // organizer (assignedTo/createdBy) or an admin can perform them.
  const updateStatus = async (ev: MyEvent, status: string) => {
    if (!(await confirmDialog(`Set event "${ev.title}" status to "${status}"?`))) return;
    setSavingStatus(true);
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetch(`/api/events/${ev.eventId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update status');
      setSelectedEvent({ ...ev, status });
      setEvents(prev => prev.map(e => e.eventId === ev.eventId ? { ...e, status } : e));
      fetchMyEvents();
    } catch (err: any) {
      notifyToast('error', 'Failed to update event: ' + err.message);
    } finally {
      setSavingStatus(false);
    }
  };

  const openEvent = (ev: MyEvent) => {
    setSelectedEvent(ev);
    loadApplicant(ev.applicantId);
  };

  // ── Calendar view helpers ──────────────────────────────────────────
  const year = calendarMonth.getFullYear();
  const month = calendarMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const eventsByDay = (day: number) => filteredEvents.filter(ev => {
    const d = new Date(ev.dateTime || 0);
    return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
  });

  return (
    <div className="space-y-5 animate-fade-in text-slate-800 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-extrabold font-display tracking-tight text-slate-900 flex items-center gap-2">
            <Calendar className="h-5 w-5 text-brand" />
            My Events
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Events assigned to you or created by you</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
            {(['list', 'calendar', 'availability'] as const).map(l => (
              <button
                key={l}
                onClick={() => setLayoutView(l)}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer capitalize ${
                  layoutView === l ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {l === 'list' ? 'List View' : l === 'calendar' ? 'Calendar View' : 'Availability Schedule'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* View filter tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        {(['upcoming', 'past', 'all'] as const).map(mode => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer capitalize ${
              viewMode === mode ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {mode}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : layoutView === 'availability' ? (
        <AgentAvailabilityPanel />
      ) : layoutView === 'calendar' ? (
        <SchedulingCalendar
          initialEvents={events.map(ev => {
            const startTime = ev.dateTime || new Date().toISOString();
            const startDateObj = new Date(startTime);
            const endDateObj = new Date(startDateObj.getTime() + 60 * 60 * 1000); // default +1 hour duration
            const endTime = endDateObj.toISOString();

            const type = (ev.eventType || '').toLowerCase();
            const calendarId = ['embassy', 'interview', 'orientation', 'training', 'flight', 'medical', 'other'].includes(type)
              ? type
              : 'other';

            return {
              id: ev.eventId,
              title: ev.title,
              startTime,
              endTime,
              calendarId,
              status: ev.status,
              assignedTo: ev.assignedToName || ev.assignedTo,
              location: ev.location,
              description: ev.description,
            };
          })}
          onEventChange={(updated) => {
            // Trigger local events update if user moves/updates on SchedulingCalendar
            const matched = updated.map(up => {
              const matchOrig = events.find(o => o.eventId === up.id);
              if (matchOrig) {
                return {
                  ...matchOrig,
                  title: up.title,
                  dateTime: up.startTime,
                  location: up.location,
                  description: up.description,
                };
              }
              return {
                eventId: up.id,
                title: up.title,
                eventType: 'Interview',
                dateTime: up.startTime,
                timezone: 'America/New_York',
                status: 'Scheduled',
                location: up.location,
                description: up.description,
              };
            });
            setEvents(matched);
          }}
          userRole={userRole}
          title="My Events Master View"
        />
      ) : filteredEvents.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
          <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
          <h3 className="text-sm font-bold text-slate-700">No {viewMode !== 'all' ? viewMode : ''} events</h3>
          <p className="text-xs text-slate-400 mt-1">Events assigned to you will appear here.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredEvents.map(ev => (
            <div
              key={ev.eventId}
              className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer"
              onClick={() => openEvent(ev)}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-bold text-slate-900 truncate">{ev.title}</h4>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[ev.status] || 'bg-slate-100 text-slate-600'}`}>{ev.status}</span>
                    {ev.isGroupEvent && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 flex items-center gap-1">
                        <Users className="h-3 w-3" /> Group Event
                      </span>
                    )}
                    {ev.isVirtual && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 flex items-center gap-1">
                        <Video className="h-3 w-3" /> Virtual
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                    {ev.dateTime && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(ev.dateTime).toLocaleString(undefined, {
                          weekday: 'short', month: 'short', day: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    )}
                    {ev.isGroupEvent && (
                      <span className="flex items-center gap-1 text-indigo-600 font-semibold">
                        <Users className="h-3 w-3" />
                        {groupBookedCount(ev)}/{groupSlotCount(ev)} slots booked
                      </span>
                    )}
                    {ev.location && (
                      <span className="flex items-center gap-1">
                        {ev.isVirtual ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                        {ev.isVirtual ? 'Virtual Meeting' : ev.location}
                      </span>
                    )}
                    {groupTeamNames(ev).length > 0 && (
                      <span className="text-brand font-medium">Team: {groupTeamNames(ev).join(', ')}</span>
                    )}
                    {!ev.isGroupEvent && ev.assignedToName && (
                      <span className="text-brand font-medium">Assigned to: {ev.assignedToName}</span>
                    )}
                  </div>
                </div>
                {ev.isVirtual && ev.meetingUrl && (
                  <a
                    href={ev.meetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="shrink-0 p-2 bg-brand/10 text-brand rounded-xl hover:bg-brand/20 transition-all"
                    title="Join meeting"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event detail modal — shared Modal (device-relative, correct z-index) */}
      {selectedEvent && (
        <Modal onClose={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-900">{selectedEvent.title}</h3>
              <button onClick={() => setSelectedEvent(null)} className="p-1.5 hover:bg-slate-100 rounded-lg cursor-pointer">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {selectedEvent.dateTime && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="font-semibold">
                    {new Date(selectedEvent.dateTime).toLocaleString(undefined, {
                      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
              {selectedEvent.location && (
                <div className="flex items-center gap-2 text-sm">
                  {selectedEvent.isVirtual ? <Video className="h-4 w-4 text-purple-500" /> : <MapPin className="h-4 w-4 text-slate-400" />}
                  <span className="text-slate-600 break-all">{selectedEvent.location}</span>
                </div>
              )}
              {selectedEvent.isGroupEvent && (
                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-3 text-xs text-indigo-900 space-y-1.5">
                  <div className="flex items-center justify-between font-bold">
                    <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5 text-indigo-500" /> Group Event</span>
                    <span>{groupBookedCount(selectedEvent)}/{groupSlotCount(selectedEvent)} slots booked</span>
                  </div>
                  {selectedEvent.timezone && <div className="text-indigo-600">Timezone: {selectedEvent.timezone}</div>}
                  {groupTeamNames(selectedEvent).length > 0 && (
                    <div className="text-indigo-700">Assigned team: {groupTeamNames(selectedEvent).join(', ')}</div>
                  )}
                  {selectedEvent.status !== 'Cancelled' && (
                    <a
                      href={`${window.location.origin}/group-schedule/${selectedEvent.eventId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center px-4 py-2 mt-1 bg-white border border-indigo-200 hover:bg-indigo-50 text-indigo-700 rounded-lg font-bold transition-all"
                    >
                      <ExternalLink className="h-3.5 w-3.5 inline mr-1.5" />Open booking page
                    </a>
                  )}
                </div>
              )}
              {selectedEvent.isVirtual && selectedEvent.meetingUrl && (
                <a
                  href={selectedEvent.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full text-center px-4 py-3 bg-success hover:bg-[color-mix(in_srgb,var(--color-success)_90%,#000)] text-white rounded-xl text-sm font-bold transition-all"
                >
                  <Video className="h-4 w-4 inline mr-2" />
                  Join Virtual Meeting
                </a>
              )}
              {selectedEvent.description && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Description</p>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap">{selectedEvent.description}</p>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className={`px-2 py-0.5 rounded-full font-bold capitalize ${STATUS_STYLES[selectedEvent.status] || 'bg-slate-100 text-slate-600'}`}>{selectedEvent.status}</span>
                <span>Type: {selectedEvent.eventType}</span>
                {selectedEvent.assignedToName && <span>· Assigned to: {selectedEvent.assignedToName}</span>}
              </div>

              {/* Applicant summary + View Applicant */}
              {selectedEvent.applicantId && (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Applicant</p>
                    <button
                      onClick={() => {
                        // In the admin context this opens the FULL applicant
                        // detail modal (same as the main Events page preview).
                        if (onViewApplicant && applicantApp) onViewApplicant(applicantApp);
                        else setShowApplicantPreview(true);
                      }}
                      disabled={!applicantApp}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold text-brand border border-brand/30 hover:bg-brand-active rounded-lg transition-all cursor-pointer disabled:opacity-50"
                    >
                      <User className="h-3 w-3" />
                      View Applicant
                    </button>
                  </div>
                  {applicantLoading ? (
                    <Loader className="h-4 w-4 animate-spin text-slate-400" />
                  ) : applicantApp ? (
                    <div className="space-y-1.5 text-xs">
                      <p className="font-bold text-slate-800">
                        {applicantApp.personalDetails?.firstName || '—'} {applicantApp.personalDetails?.lastName || ''}
                      </p>
                      <p className="flex items-center gap-1.5 text-slate-500">
                        <Mail className="h-3 w-3" /> {applicantApp.userEmail || applicantApp.personalDetails?.email || '—'}
                      </p>
                      <p className="flex items-center gap-1.5 text-slate-500">
                        <BookOpen className="h-3 w-3" />
                        {applicantApp.programType || applicantApp.programPreferences?.programType || '—'}
                        {applicantApp.programTerm ? ` · ${applicantApp.programTerm}` : ''}
                      </p>
                      <p className="flex items-center gap-1.5 text-slate-500">
                        <Globe className="h-3 w-3" />
                        {applicantApp.personalDetails?.address?.country
                          || applicantApp.personalDetails?.countryOfCitizenship
                          || applicantApp.personalDetails?.currentCountryOfResidence
                          || '—'}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-400 italic">Applicant details unavailable</p>
                  )}
                </div>
              )}

              {/* Organizer / moderator controls */}
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Organizer Controls</p>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={selectedEvent.status}
                    onChange={e => updateStatus(selectedEvent, e.target.value)}
                    disabled={savingStatus}
                    className="px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white font-semibold text-slate-700 cursor-pointer"
                  >
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {selectedEvent.status !== 'Cancelled' && (
                    <button
                      onClick={() => updateStatus(selectedEvent, 'Cancelled')}
                      disabled={savingStatus}
                      className="px-3 py-2 text-xs font-bold text-rose-600 border border-rose-200 hover:bg-rose-50 rounded-xl transition-all cursor-pointer"
                    >
                      Cancel Event
                    </button>
                  )}
                  {selectedEvent.status === 'Cancelled' && (
                    <button
                      onClick={() => updateStatus(selectedEvent, 'Scheduled')}
                      disabled={savingStatus}
                      className="px-3 py-2 text-xs font-bold text-emerald-600 border border-emerald-200 hover:bg-emerald-50 rounded-xl transition-all cursor-pointer"
                    >
                      Reactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Nested "View Applicant" preview — front-and-centre modal matching the
          main Events page preview */}
      {showApplicantPreview && applicantApp && (
        <ApplicantPreviewModal
          app={applicantApp}
          onClose={() => setShowApplicantPreview(false)}
          centered
        />
      )}
    </div>
  );
}

// ── Agent Availability Settings Panel ────────────────────────────
function AgentAvailabilityPanel() {
  const [availability, setAvailability] = useState<any>(null);
  const [bookingAccessEnabled, setBookingAccessEnabled] = useState<boolean>(true);
  const [uniqueBookingUrl, setUniqueBookingUrl] = useState<string>('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [timezone, setTimezone] = useState('America/New_York');
  const [weeklySlots, setWeeklySlots] = useState<{ dayOfWeek: number; startTime: string; endTime: string }[]>([]);
  const [bufferMinutes, setBufferMinutes] = useState(15);
  const [minNoticeHours, setMinNoticeHours] = useState(24);
  const [maxDaysAdvance, setMaxDaysAdvance] = useState(30);
  const [isActive, setIsActive] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dateOverrides, setDateOverrides] = useState<{ date: string; available: boolean; startTime?: string; endTime?: string; reason?: string }[]>([]);

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  useEffect(() => {
    (async () => {
      try {
        const token = await auth.currentUser?.getIdToken();
        const res = await fetch('/api/availability/mine', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setBookingAccessEnabled(data.bookingAccessEnabled !== false);
          if (data.uniqueBookingUrl) {
            setUniqueBookingUrl(window.location.origin + data.uniqueBookingUrl);
          } else if (auth.currentUser?.uid) {
            setUniqueBookingUrl(`${window.location.origin}/book-agent/${auth.currentUser.uid}`);
          }
          if (data.availability) {
            setAvailability(data.availability);
            setTimezone(data.availability.timezone || 'America/New_York');
            setWeeklySlots(data.availability.weeklySlots || []);
            setBufferMinutes(data.availability.bufferMinutes || 15);
            setMinNoticeHours(data.availability.minNoticeHours || 24);
            setMaxDaysAdvance(data.availability.maxDaysAdvance || 30);
            setIsActive(data.availability.isActive !== false);
            setDateOverrides(data.availability.dateOverrides || []);
          }
        }
      } catch (err) {
        console.error('Failed to load availability:', err);
      }
      setLoading(false);
    })();
  }, []);

  const handleCopyLink = () => {
    if (!uniqueBookingUrl) return;
    navigator.clipboard.writeText(uniqueBookingUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const addSlot = () => {
    setWeeklySlots(prev => [...prev, { dayOfWeek: 1, startTime: '09:00', endTime: '17:00' }]);
  };

  const removeSlot = (idx: number) => {
    setWeeklySlots(prev => prev.filter((_, i) => i !== idx));
  };

  const updateSlot = (idx: number, field: string, value: any) => {
    setWeeklySlots(prev => prev.map((s, i) => i === idx ? { ...s, [field]: value } : s));
  };

  const addOverride = () => {
    setDateOverrides(prev => [...prev, { date: new Date().toISOString().split('T')[0], available: true, startTime: '09:00', endTime: '17:00', reason: '' }]);
  };

  const removeOverride = (idx: number) => {
    setDateOverrides(prev => prev.filter((_, i) => i !== idx));
  };

  const updateOverride = (idx: number, field: string, value: any) => {
    setDateOverrides(prev => prev.map((o, i) => i === idx ? { ...o, [field]: value } : o));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) { setMessage({ type: 'error', text: 'Not authenticated — please refresh' }); setSaving(false); return; }
      const res = await fetch('/api/availability/mine', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          timezone,
          weeklySlots,
          dateOverrides,
          bufferMinutes,
          minNoticeHours,
          maxDaysAdvance,
          isActive,
        }),
      });
      if (res.ok) {
        setMessage({ type: 'success', text: 'Availability saved successfully!' });
      } else {
        const err = await res.json();
        setMessage({ type: 'error', text: err.error || 'Failed to save' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    }
    setSaving(false);
    setTimeout(() => setMessage(null), 3000);
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 flex items-center justify-center">
        <Loader className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden space-y-0">
      {/* Header */}
      <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-50 to-indigo-50/30 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">My Availability & Unique Booking Link</h3>
          <p className="text-xs text-slate-500 mt-0.5">Set your weekly schedule and share your unique meeting link</p>
        </div>
        <div className="flex items-center gap-2">
          {bookingAccessEnabled ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle className="h-3.5 w-3.5 text-emerald-600" /> Booking Access Enabled
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
              <Lock className="h-3.5 w-3.5 text-amber-600" /> Booking Access Disabled
            </span>
          )}
        </div>
      </div>

      {/* Unique Booking Link Card */}
      <div className="p-5 border-b border-slate-100 bg-slate-50/50">
        <div className="space-y-2">
          <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
            <Link className="h-3.5 w-3.5 text-brand" /> Your Unique Booking Link
          </label>

          {bookingAccessEnabled ? (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <input
                type="text"
                readOnly
                value={uniqueBookingUrl}
                className="flex-1 px-3.5 py-2 text-xs border border-slate-200 rounded-xl bg-white text-slate-700 font-mono focus:outline-none focus:ring-[3px] focus:ring-brand-primary/15 focus:border-brand-primary"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className="px-3.5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-2xs"
                >
                  {copiedLink ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5 text-slate-500" />}
                  {copiedLink ? 'Copied!' : 'Copy Link'}
                </button>
                <a
                  href={uniqueBookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3.5 py-2 bg-brand text-white hover:bg-brand/90 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer shadow-2xs"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  View Booking Page
                </a>
              </div>
            </div>
          ) : (
            <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs space-y-1">
              <p className="font-bold flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5 text-amber-600 shrink-0" /> Booking access is currently disabled for your account
              </p>
              <p className="text-amber-800 leading-relaxed">
                Super Administrators have disabled public bookings for your account. Applicants cannot schedule new meetings via your link.
                Existing scheduled meetings and previous booking records remain completely intact.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Timezone */}
        <div>
          <label className="text-xs font-bold text-slate-600 block mb-1">Timezone</label>
          <div className="max-w-xs">
            <SearchableSelect
              value={timezone}
              onChange={tz => setTimezone(tz || 'UTC')}
              options={ALL_TIMEZONES.map(tz => ({ value: tz, label: tz }))}
              placeholder="Search timezone (e.g. Lagos, London, Tokyo)..."
              allowClear={false}
            />
          </div>
        </div>

        {/* Weekly Slots */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold text-slate-600">Weekly Availability</label>
            <button onClick={addSlot} className="text-xs font-bold text-brand hover:text-blue-700 flex items-center gap-1 cursor-pointer">
              <Plus className="h-3 w-3" /> Add Time Slot
            </button>
          </div>
          <div className="space-y-2">
            {weeklySlots.map((slot, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-slate-50 rounded-xl p-3">
                <select value={slot.dayOfWeek} onChange={e => updateSlot(idx, 'dayOfWeek', parseInt(e.target.value))}
                  className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white">
                  {DAY_NAMES.map((name, di) => <option key={di} value={di}>{name}</option>)}
                </select>
                <input type="time" value={slot.startTime} onChange={e => updateSlot(idx, 'startTime', e.target.value)}
                  className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white w-24" />
                <span className="text-xs text-slate-400">to</span>
                <input type="time" value={slot.endTime} onChange={e => updateSlot(idx, 'endTime', e.target.value)}
                  className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white w-24" />
                <button onClick={() => removeSlot(idx)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {weeklySlots.length === 0 && (
              <p className="text-xs text-slate-400 italic py-2">No regular hours set. Add your available time slots above.</p>
            )}
          </div>
        </div>

        {/* Date Overrides */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-bold text-slate-600">Date Specific Availability & Overrides</label>
            <button onClick={addOverride} className="text-xs font-bold text-brand hover:text-blue-700 flex items-center gap-1 cursor-pointer">
              <Plus className="h-3 w-3" /> Add Specific Date
            </button>
          </div>
          <div className="space-y-2">
            {dateOverrides.map((override, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-2 bg-amber-50/70 border border-amber-200/60 rounded-xl p-3">
                <input
                  type="date"
                  value={override.date}
                  onChange={e => updateOverride(idx, 'date', e.target.value)}
                  className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white font-medium"
                />
                <select
                  value={override.available ? 'available' : 'blocked'}
                  onChange={e => {
                    const isAvail = e.target.value === 'available';
                    updateOverride(idx, 'available', isAvail);
                    if (isAvail && (!override.startTime || !override.endTime)) {
                      updateOverride(idx, 'startTime', '09:00');
                      updateOverride(idx, 'endTime', '17:00');
                    }
                  }}
                  className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white font-semibold"
                >
                  <option value="available">Date Specific Available</option>
                  <option value="blocked">Blocked Date (Unavailable)</option>
                </select>

                {override.available && (
                  <div className="flex items-center gap-1 bg-white px-2 py-1 border border-slate-200 rounded-lg">
                    <input
                      type="time"
                      value={override.startTime || '09:00'}
                      onChange={e => updateOverride(idx, 'startTime', e.target.value)}
                      className="text-xs font-mono border-none outline-none w-20"
                    />
                    <span className="text-xs text-slate-400">to</span>
                    <input
                      type="time"
                      value={override.endTime || '17:00'}
                      onChange={e => updateOverride(idx, 'endTime', e.target.value)}
                      className="text-xs font-mono border-none outline-none w-20"
                    />
                  </div>
                )}

                <input
                  type="text"
                  value={override.reason || ''}
                  onChange={e => updateOverride(idx, 'reason', e.target.value)}
                  placeholder="Reason / Label (e.g. Special Interview Day)"
                  className="px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg bg-white flex-1 min-w-[140px]"
                />

                <button
                  type="button"
                  onClick={() => removeOverride(idx)}
                  className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            {dateOverrides.length === 0 && (
              <p className="text-xs text-slate-400 italic py-1">No date-specific availability overrides added.</p>
            )}
          </div>
        </div>

        {/* Settings */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-bold text-slate-600 block mb-1">Buffer (min)</label>
            <input type="number" min={0} max={120} value={bufferMinutes} onChange={e => setBufferMinutes(parseInt(e.target.value) || 15)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 block mb-1">Min Notice (hrs)</label>
            <input type="number" min={0} max={168} value={minNoticeHours} onChange={e => setMinNoticeHours(parseInt(e.target.value) || 24)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" />
          </div>
          <div>
            <label className="text-xs font-bold text-slate-600 block mb-1">Max Advance (days)</label>
            <input type="number" min={1} max={90} value={maxDaysAdvance} onChange={e => setMaxDaysAdvance(parseInt(e.target.value) || 30)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white" />
          </div>
        </div>

        {/* Active toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand-primary" />
          <span className="text-xs font-semibold text-slate-600">Accept scheduling requests</span>
        </label>

        {/* Message */}
        {message && (
          <div className={`flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-xl ${
            message.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
            {message.text}
          </div>
        )}

        <button onClick={handleSave} disabled={saving}
          className="px-5 py-2 btn-primary text-white text-xs font-bold rounded-xl transition-all cursor-pointer disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Availability'}
        </button>
      </div>
    </div>
  );
}
